import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Eyebrow } from "@/components/shared/Primitives";
import { Reveal, SplitLines, Stagger, Item } from "@/components/motion/Reveal";
import { useBrokers } from "@/context/BrokersContext";

const PILLARS = [
  ["Safety & regulation", 30, "Regulatory tier of the entity serving your country, custody arrangements, operating history and public enforcement record."],
  ["Realized trading costs", 25, "Measured spreads plus commission, sampled hourly across London and New York sessions — never advertised minimums."],
  ["Holding costs & swaps", 20, "Published swap tables normalized to account currency, including triple-swap schedules and swap-free terms."],
  ["Funding & reliability", 15, "Verified withdrawal timing from user-submitted receipts, funding breadth and fee transparency."],
  ["Platform & service", 10, "Platform availability, execution latency and support responsiveness."],
];

const FAQ = [
  ["Who operates ForexBrokerSignals?", "An independent research team of former sell-side FX analysts and quantitative developers. We are not owned by, or affiliated with, any broker."],
  ["How do you make money?", "Through referral commissions when a visitor opens an account via our links. Commission levels have no input into scores, ordering or editorial coverage. Every referral link is labelled."],
  ["How often is data refreshed?", "Spreads are sampled hourly, swaps daily at 22:00 UTC (or live where a broker publishes a feed), regulatory registers monthly and withdrawal benchmarks quarterly."],
  ["What data do you collect from me?", "Finder inputs are processed in your browser and are not stored on our servers in this phase. See the privacy policy for detail."],
];

export default function About() {
  const { brokers: BROKERS } = useBrokers();
  return (
    <div data-testid="about-page">
      <section className="container-x py-16 lg:py-24">
        <Eyebrow className="mb-4">About & methodology</Eyebrow>
        <SplitLines lines={["Independent by design.", "Transparent by method."]} className="t-hero max-w-5xl" />
        <Reveal delay={0.3} className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <p className="lg:col-span-7 text-lg leading-relaxed text-[var(--ink-2)]">ForexBrokerSignals exists because choosing a broker is mostly a cost and trust problem that affiliate directories have little incentive to solve honestly. We publish how we score, how we verify, how often we refresh, and exactly how we are paid.</p>
          <div className="lg:col-span-5 lg:col-start-8 grid grid-cols-3 gap-4 font-mono text-sm">
            <div><p className="t-stat">{BROKERS.length}</p><p className="t-stat-label">Brokers verified</p></div>
            <div><p className="t-stat">{BROKERS.reduce((n, b) => n + b.entities.length, 0)}</p><p className="t-stat-label">Legal entities</p></div>
            <div><p className="t-stat">0</p><p className="t-stat-label">Paid placements</p></div>
          </div>
        </Reveal>
      </section>

      <section className="bg-white border-y border-[var(--line)]">
        <div className="container-x py-16 lg:py-24 grid grid-cols-1 gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow className="mb-4">Recommendation score</Eyebrow>
            <h2 className="t-h3">Five weighted pillars. One auditable number.</h2>
            <p className="mt-4 text-sm leading-relaxed text-[var(--ink-3)]">Your finder answers re-weight these pillars toward what you marked essential. The underlying measurements never change per user — only the weights.</p>
          </Reveal>
          <Stagger className="lg:col-span-8 divide-y divide-[var(--line)]">
            {PILLARS.map(([t, w, d]) => (
              <Item key={t} className="grid grid-cols-12 gap-4 py-6">
                <div className="col-span-3 sm:col-span-2 t-stat text-[var(--cobalt)]">{w}%</div>
                <div className="col-span-9 sm:col-span-10"><p className="font-medium">{t}</p><p className="mt-1 text-sm text-[var(--ink-3)] leading-relaxed">{d}</p><div className="mt-3 h-1 w-full rounded-full bg-[var(--canvas-cool)]"><div className="h-full rounded-full bg-[var(--cobalt)]" style={{ width: `${w * 3}%` }} /></div></div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="container-x py-16 lg:py-24 grid grid-cols-1 gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-4"><Eyebrow className="mb-4">Verification & disclosure</Eyebrow><h2 className="t-h3">Questions we expect you to ask.</h2></Reveal>
        <Stagger className="lg:col-span-8 divide-y divide-[var(--line)]">
          {FAQ.map(([q, a]) => <Item key={q} className="py-6"><p className="font-medium">{q}</p><p className="mt-2 text-sm leading-relaxed text-[var(--ink-3)]">{a}</p></Item>)}
          <Item className="py-6 flex flex-wrap gap-4 text-sm">
            <Link to="/legal/ranking-methodology" className="link-arrow">Ranking methodology <ArrowRight size={14} /></Link>
            <Link to="/legal/data-methodology" className="link-arrow">Data methodology <ArrowRight size={14} /></Link>
            <Link to="/legal/affiliate-disclosure" className="link-arrow">Affiliate disclosure <ArrowRight size={14} /></Link>
          </Item>
        </Stagger>
      </section>
    </div>
  );
}
